import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { concat, map, of, shareReplay, skip } from 'rxjs';
import { endpoints } from '../../utils/endpoints';
import {
    PublishTournamentMatchResultRequest,
    RemoveScreenshotBySupervisorRequest,
    ReportTournamentMatchResultRequest,
    SaveTournamentMatchGameRequest,
    SaveTournamentMatchRequest,
    RemoveScreenshotBySupervisorRequestV2,
} from '../models/api/requests/tournamentBrackets.request';
import { DistributeParticipantsRequest } from '../models/api/requests/tournamentGeneral.request';
import {
    ManualDistributedEntry,
    MatchGameResponse,
    MatchResponse,
    TournamentMatchGameRoundResultResponse,
    TournamentMatchResponse,
    TournamentMatchSubmittedScreenshotResponse,
} from '../models/api/responses/tournamentBrackets.response';
import { BracketType, MatchFormat } from '../models/enums/bracketType.enum';

@Injectable({
    providedIn: 'root',
})
export class TournamentBracketsService {
    private http = inject(HttpClient);


  fetchStageLevels(tournamentProjectId: string) {
    return this.http.get<any>(
      endpoints.builder.children.getProjectDataById(tournamentProjectId)
    ).pipe(
      map((project: any) => project?.stageLevels ?? [])
    );
  }

  fetchStageById(stageId: string) {
    return this.http.get<any>(
      endpoints.builder.children.getStageById(stageId)
    ).pipe(shareReplay());
  }

  fetchRoundsByStageId(stageId: string) {
    return this.http.get<any>(
      endpoints.builder.children.getRounds(stageId)
    ).pipe(shareReplay());
  }

    getTournamentBrackets(tournamentId: string) {
        return this.http
            .get<TournamentMatchResponse[]>(
                endpoints.tournaments.children.getTournamentMatches(
                    tournamentId
                )
            )
            .pipe(shareReplay());
    }

    fetchTournamentMatch({
        tournamentId,
        matchId,
    }: {
        tournamentId: string;
        matchId: string;
    }) {
        return this.http
            .get<MatchResponse>(
                endpoints.matches.children.getTournamentMatchUrl(
                    tournamentId,
                    matchId
                )
            )
            .pipe(shareReplay());
    }

    fetchTournamentMatchV2({
        tournamentId,
        matchId,
    }: {
        tournamentId: string;
        matchId: string;
    }) {
        return this.http
            .get<MatchResponse>(
                endpoints.matches.children.getTournamentMatchUrlV2(
                    tournamentId,
                    matchId
                )
            )
            .pipe(shareReplay());
    }

    fetchTournamentGroupMatchV2({
        tournamentId,
        groupId,
    }: {
        tournamentId: string;
        groupId: string;
    }) {
        return this.http
            .get<MatchResponse>(
                endpoints.matches.children.getTournamentMatchUrlV2(
                    tournamentId,
                    groupId
                )
            )
            .pipe(shareReplay());
    }

    fetchTournamentMatchGame({
        tournamentId,
        matchId,
        matchGameId,
    }: {
        tournamentId: string;
        matchId: string;
        matchGameId: string;
    }) {
        return this.http
            .get<MatchGameResponse>(
                endpoints.matches.children.getTournamentMatchGameUrl(
                    tournamentId,
                    matchId,
                    matchGameId
                )
            )
            .pipe(shareReplay());
    }

    fetchTournamentGroupMatchGameV2({
        tournamentId,
        groupId,
        matchId,
    }: {
        tournamentId: string;
        groupId: string;
        matchId: string;
    }) {
        return this.http
            .get<TournamentMatchResponse>(
                endpoints.matches.children.getTournamentGroupMatchUrlV2(
                    tournamentId,
                    groupId,
                    matchId
                )
            )
            .pipe(shareReplay());
    }

    fetchTournamentMatchGameV2({
        tournamentId,
        matchId,
        matchGameId,
    }: {
        tournamentId: string;
        matchId: string;
        matchGameId: string;
    }) {
        return this.http
            .get<MatchGameResponse>(
                endpoints.matches.children.getTournamentMatchGameUrlV2(
                    tournamentId,
                    matchId,
                    matchGameId
                )
            )
            .pipe(shareReplay());
    }

    saveTournamentMatch(
        tournamentId: string,
        matchId: string,
        requestData: SaveTournamentMatchRequest
    ) {
        return this.http.put<MatchResponse>(
            endpoints.matches.children.getTournamentMatchUrl(
                tournamentId,
                matchId
            ),
            requestData
        );
    }

    saveTournamentMatchGame(
        tournamentId: string,
        matchId: string,
        matchGameId: string,
        requestData: SaveTournamentMatchGameRequest
    ) {
        return this.http.put<MatchGameResponse>(
            endpoints.matches.children.getTournamentMatchGameUrl(
                tournamentId,
                matchId,
                matchGameId
            ),
            requestData
        );
    }

    saveTournamentGroupMatchV2(
        tournamentId: string,
        groupId: string,
        matchId: string,
        requestData: SaveTournamentMatchGameRequest
    ) {
        return this.http.put<MatchResponse>(
            endpoints.matches.children.getTournamentGroupMatchUrlV2(
                tournamentId,
                groupId,
                matchId
            ),
            requestData
        );
    }

    removeScreenshotBySupervisor(request: RemoveScreenshotBySupervisorRequest) {
        return this.http
            .delete<unknown>(
                endpoints.matches.children.getTournamentMatchResultScreenshotUrl(
                    request.tournamentId,
                    request.matchId,
                    request.matchGameId,
                    request.screenshotId
                )
            )
            .pipe(shareReplay());
    }

    removeGroupScreenshotBySupervisorV2(
        request: RemoveScreenshotBySupervisorRequestV2
    ) {
        return this.http
            .delete<unknown>(
                endpoints.matches.children.getTournamentGroupMatchResultScreenshotUrlV2(
                    request.tournamentId,
                    request.groupId,
                    request.matchId,
                    request.screenshotId
                )
            )
            .pipe(shareReplay());
    }

    reportTournamentMatchResults({
        tournamentId,
        matchId,
        matchGameId,
        participantId,
        requestData,
        screenshots,
    }: {
        tournamentId: string;
        matchId: string;
        matchGameId: string;
        participantId: string;
        requestData: ReportTournamentMatchResultRequest;
        screenshots: File[];
    }) {
        const formData = new FormData();
        screenshots.forEach((s) => {
            formData.append('formFiles', s);
        });
        let httpParams = new HttpParams();
        httpParams = httpParams.append('participantId', participantId);

        return concat(
            this.http.post(
                endpoints.matches.children.getTournamentMatchResultScreenshotsUrl(
                    tournamentId,
                    matchId,
                    matchGameId
                ),
                formData,
                {
                    params: httpParams,
                }
            ),
            this.http.post<MatchResponse>(
                endpoints.matches.children.getTournamentMatchResultUrl(
                    tournamentId,
                    matchId,
                    matchGameId
                ),
                requestData
            )
        ).pipe(
            skip(1),
            map((response) => response as MatchResponse),
            shareReplay()
        );
    }

    reportTournamentMatchResultsV2({
        tournamentId,
        groupId,
        matchId,
        participantId,
        requestData,
        screenshots,
    }: {
        tournamentId: string;
        groupId: string;
        matchId: string;
        participantId: string;
        requestData: ReportTournamentMatchResultRequest;
        screenshots: File[];
    }) {
        const formData = new FormData();
        screenshots.forEach((s) => {
            formData.append('formFiles', s);
        });
        let httpParams = new HttpParams();
        httpParams = httpParams.append('participantId', participantId);

        return concat(
            this.http.post(
                endpoints.matches.children.getTournamentGroupMatchResultScreenshotsUrlV2(
                    tournamentId,
                    groupId,
                    matchId
                ),
                formData,
                {
                    params: httpParams,
                }
            ),
            this.http.post<MatchResponse>(
                endpoints.matches.children.getTournamentGroupMatchResultUrlV2(
                    tournamentId,
                    groupId,
                    matchId
                ),
                requestData
            )
        ).pipe(
            skip(1),
            map((response) => response as MatchResponse),
            shareReplay()
        );
    }

    fetchSubmittedScreenshots({
        tournamentId,
        matchId,
        matchGameId,
    }: {
        tournamentId: string;
        matchId: string;
        matchGameId: string;
    }) {
        return this.http
            .get<TournamentMatchSubmittedScreenshotResponse[]>(
                endpoints.matches.children.getTournamentMatchResultScreenshotsUrl(
                    tournamentId,
                    matchId,
                    matchGameId
                )
            )
            .pipe(
                map((screenshots) =>
                    screenshots.map(
                        (s): TournamentMatchSubmittedScreenshotResponse => ({
                            ...s,
                            url: `${endpoints.matches.children.getTournamentMatchResultScreenshotUrl(
                                tournamentId,
                                matchId,
                                matchGameId,
                                s.id
                            )}?ts=${Date.now()}`,
                        })
                    )
                ),
                shareReplay()
            );
    }

    fetchSubmittedGroupScreenshotsV2({
        tournamentId,
        groupId,
        matchId,
    }: {
        tournamentId: string;
        groupId: string;
        matchId: string;
    }) {
        return this.http
            .get<TournamentMatchSubmittedScreenshotResponse[]>(
                endpoints.matches.children.getTournamentGroupMatchResultScreenshotsUrlV2(
                    tournamentId,
                    groupId,
                    matchId
                )
            )
            .pipe(
                map((screenshots) =>
                    screenshots.map(
                        (s): TournamentMatchSubmittedScreenshotResponse => ({
                            ...s,
                            url: `${endpoints.matches.children.getTournamentGroupMatchResultScreenshotUrlV2(
                                tournamentId,
                                groupId,
                                matchId,
                                s.id
                            )}?ts=${Date.now()}`,
                        })
                    )
                ),
                shareReplay()
            );
    }

    fetchGameRoundsResult({
        tournamentId,
        matchId,
        matchGameId,
    }: {
        tournamentId: string;
        matchId: string;
        matchGameId: string;
    }) {
        return this.http
            .get<TournamentMatchGameRoundResultResponse[]>(
                endpoints.matches.children.getTournamentMatchResultsUrl(
                    tournamentId,
                    matchId,
                    matchGameId
                )
            )
            .pipe(shareReplay());
    }

    fetchGroupGameRoundsResultV2({
        tournamentId,
        groupId,
        matchId,
    }: {
        tournamentId: string;
        groupId: string;
        matchId: string;
    }) {
        return this.http
            .get<TournamentMatchGameRoundResultResponse[]>(
                endpoints.matches.children.getTournamentGroupMatchResultsUrlV2(
                    tournamentId,
                    groupId,
                    matchId
                )
            )
            .pipe(shareReplay());
    }

    supervisorReportTournamentMatchResult({
        publish,
        tournamentId,
        matchId,
        matchGameId,
        requestData,
        screenshots,
    }: {
        publish?: boolean;
        tournamentId: string;
        matchId: string;
        matchGameId: string;
        requestData?: PublishTournamentMatchResultRequest;
        screenshots?: File[];
    }) {
        const formData = new FormData();
        let httpParams = new HttpParams();

        if (requestData?.matchParticipantResults) {
            screenshots?.forEach((s) => {
                formData.append('formFiles', s);
            });

            httpParams = httpParams.append(
                'participantId',
                requestData.matchParticipantResults[0].participantId ?? ''
            );
        }

        return concat(
            screenshots?.length
                ? this.http.post(
                      endpoints.matches.children.getTournamentMatchResultScreenshotsUrl(
                          tournamentId,
                          matchId,
                          matchGameId
                      ),
                      formData,
                      {
                          params: httpParams,
                      }
                  )
                : of([]),
            this.http.post<MatchResponse>(
                publish
                    ? endpoints.matches.children.getPublishTournamentMatchUrl(
                          tournamentId,
                          matchId
                      )
                    : endpoints.matches.children.getTournamentMatchResultUrlAsSupervisor(
                          tournamentId,
                          matchId,
                          matchGameId
                      ),
                publish ? null : requestData
            )
        ).pipe(
            skip(1),
            map((response) => response as MatchResponse),
            shareReplay()
        );
    }

    supervisorReportTournamentMatchResultV2({
        publish,
        tournamentId,
        groupId,
        matchId,
        participantId,
        requestData,
        screenshots,
    }: {
        publish?: boolean;
        tournamentId: string;
        groupId: string;
        matchId: string;
        participantId?: string;
        requestData?: any;
        screenshots?: File[];
    }) {
        const pathParticipantId = participantId;
        const resultsArray = requestData?.results ? requestData.results : [];

        const screenshotsUpload$ = (screenshots?.length)
            ? this.http.post(
                  endpoints.matches.children.getTournamentGroupMatchResultScreenshotsUrlV2(
                      tournamentId,
                      groupId,
                      matchId
                  ),
                  (() => {
                      const fd = new FormData();
                      (screenshots || []).forEach((s) => fd.append('formFiles', s));
                      return fd;
                  })(),
                  {
                      params: (() => {
                          let httpParams = new HttpParams();
                          const pid = pathParticipantId || resultsArray?.[0]?.participantId || '';
                          if (pid) httpParams = httpParams.append('participantId', pid);
                          return httpParams;
                      })(),
                  }
              )
            : of([]);

        const submitUrl = publish
            ? endpoints.matches.children.getPublishTournamentGroupMatchUrlV2(
                  tournamentId,
                  groupId
              )
            : endpoints.matches.children.getTournamentGroupMatchResultUrlAsSupervisorV2WithoutParticipant(
                  tournamentId,
                  groupId,
                  matchId
              );

        const submitBody = publish ? null : { results: resultsArray };

        const submit$ = this.http.post<MatchResponse>(submitUrl, submitBody);

        return concat(screenshotsUpload$, submit$).pipe(
            skip(1),
            map((response) => response as MatchResponse),
            shareReplay()
        );
    }

    changeRoundFormat(
        tournamentId: string,
        requestData: {
            type: BracketType;
            level: number;
            format: MatchFormat;
            rules?: string;
            gameMaps?: { [key: string]: string };
        }
    ) {
        return this.http.put<TournamentMatchResponse[]>(
            endpoints.matches.children.getChangeRoundFormatUrl(tournamentId),
            requestData
        );
    }

    getRoundDataV2(tournamentId: string, roundId: string) {
        return this.http.get<{ rules: string; numberOfMatches: number; maps: any }>(
            endpoints.matches.children.getRoundDataV2(tournamentId, roundId)
        );
    }

    changeRoundFormatV2(
        tournamentId: string,
        body: { roundId: string; rules?: string; numberOfMatches: number; maps: any[] }
    ) {
        return this.http.put<TournamentMatchResponse[]>(
            endpoints.matches.children.getRoundDataV2(tournamentId, body.roundId),
            body
        );
    }

    getAllowedMatchesForWinCondition(condition: string) {
        return this.http.get<{ condition: string; allowedValues: number[] }>(
            endpoints.matches.children.getWinConditionAllowedMatches(condition)
        );
    }

    manualDistributionFetchEntries(tournamentId: string) {
        return this.http.get<ManualDistributedEntry[]>(
            endpoints.tournaments.children.getManualDistributionEntries(
                tournamentId
            )
        );
    }

    manualDistributionGenerateEntries(tournamentId: string) {
        return this.http.post<ManualDistributedEntry[]>(
            endpoints.tournaments.children.getGenerateManualDistributionEntries(
                tournamentId
            ),
            {}
        );
    }

    manualDistributionDistributeParticipants(
        request: DistributeParticipantsRequest
    ) {
        return this.http.post<ManualDistributedEntry>(
            endpoints.tournaments.children.getDistributeParticipants(
                request.tournamentId,
                request.entryId
            ),
            { participants: request.participants }
        );
    }

    finalizeSupervisorMatchReport(
        tournamentId: string,
        matchId: string
    ) {
        return this.http.post<any>(
            endpoints.tournaments.children.finalizeSupervisorMatchReport(tournamentId, matchId),{}
        )
    }

    finalizeSupervisorMatchReportV2(
        tournamentId: string,
        groupId: string,
        matchId: string
    ) {
        return this.http.post<any>(
            endpoints.tournaments.children.finalizeSupervisorMatchReportV2(
                tournamentId,
                groupId,
                matchId
            ),
            {}
        );
    }
}
